import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import AdfImage from "../components/storefront/AdfImage";
import { useI18n } from "../lib/i18n";
import { pickLocale } from "../lib/format";
import { useSections } from "../hooks/useSiteContent";
import { useDocumentTitle } from "../lib/useDocumentTitle";

/**
 * A page an editor composes out of sections.
 *
 * Nothing renders unless the admin has published something, apart from the
 * `intro` a caller supplies — a page that invents copy the owner never wrote is
 * how the site ended up contradicting itself in the first place.
 */
export default function ContentPage({ placement, eyebrow, title, intro, canonicalPath, testid, children }) {
  const { locale } = useI18n();
  const sections = useSections(placement);
  useDocumentTitle(title);

  return (
    <div className="bg-[var(--adf-cream)] text-[var(--adf-ink)]" data-testid={testid}>
      <SeoMeta title={title} description={intro} locale={locale} canonicalPath={canonicalPath} />
      <Header />
      <main id="main" className="adf-container py-12 sm:py-16">
        <p className="adf-small text-[var(--adf-gray-2)]">{eyebrow}</p>
        <h1 className="mt-3 max-w-[22ch] font-serif text-[34px] leading-tight sm:text-[44px]">{title}</h1>
        {intro && <p className="mt-4 max-w-[62ch] text-[15px] leading-7 text-[var(--adf-gray-1)]">{intro}</p>}

        {(sections || []).map((section) => {
          const heading = pickLocale(section.title, locale);
          const sub = pickLocale(section.subtitle, locale);
          const body = pickLocale(section.body, locale);
          const cta = pickLocale(section.cta_label, locale);
          return (
            <section key={section.id} className="mt-12 border-t border-[var(--adf-gray-5)] pt-8" data-testid={`section-${section.key}`}>
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-start">
                <div>
                  {heading && <h2 className="font-serif text-[24px] leading-snug sm:text-[28px]">{heading}</h2>}
                  {sub && <p className="mt-2 text-[14px] text-[var(--adf-gray-2)]">{sub}</p>}
                  {body && (
                    <div className="mt-4 space-y-4 text-[15px] leading-7 text-[var(--adf-gray-1)]">
                      {body.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean).map((part) => (
                        <p key={part.slice(0, 40)} className="whitespace-pre-line">{part}</p>
                      ))}
                    </div>
                  )}
                  {cta && section.cta_url && (
                    <Link to={section.cta_url} className="adf-cta adf-cta-outline mt-6 inline-flex">{cta}</Link>
                  )}
                </div>
                {section.media_url && (
                  <AdfImage source={section.media_url} alt="" sizes="(max-width: 1024px) 100vw, 46vw"
                            className="w-full" style={{ aspectRatio: "4 / 3", objectFit: "cover" }} />
                )}
              </div>
            </section>
          );
        })}

        {children}
      </main>
      <Footer />
    </div>
  );
}
